"use client";

import { useState } from "react";
import { FiChevronDown } from "react-icons/fi";

import { useBookingEvent } from "@/hooks/self-schedule/use-booking-event";
import { useFrontPage } from "@/hooks/self-schedule/use-front-page";

import {
  formatSessionTimes,
  summarizeSessions,
} from "@/lib/booking-event-sessions";
import { formatDate } from "@/lib/format-data";
import { cx } from "@/lib/tailwind-utils";

import { PublicBookingEvent } from "@/types/self-schedule";

/** The open event this page is booking into, with where it runs. */
const useOpenEvent = () => {
  const { data: bookingEvent } = useBookingEvent();
  const { data: frontPage } = useFrontPage();

  // Hidden while the link isn't taking bookings: the status screen in the
  // page's place says the same things alongside why.
  if (!bookingEvent || bookingEvent.status !== "OPEN") return null;

  // Already narrowed to the event's locations.
  const names = (frontPage?.locations ?? []).map((location) => location.name);
  const where =
    bookingEvent.locationIds.length === 0 && names.length > 1
      ? "Any of our locations"
      : names.join(" or ");
  const hasEventPricing =
    Boolean(bookingEvent.promotionName) ||
    bookingEvent.services.some(
      (service) => service.originalDisplayPrice != null,
    );

  return { bookingEvent, where, hasEventPricing };
};

const EventDetails = ({
  bookingEvent,
  where,
  hasEventPricing,
}: {
  bookingEvent: PublicBookingEvent;
  where: string;
  hasEventPricing: boolean;
}) => (
  <div className="flex flex-col gap-3 text-body">
    {bookingEvent.description && (
      <p className="text-foreground-secondary">{bookingEvent.description}</p>
    )}
    <dl className="flex flex-col gap-3">
      <div className="flex flex-col gap-0.5">
        <dt className="text-caption text-foreground-tertiary">When</dt>
        {bookingEvent.sessions.map((session) => (
          <dd key={`${session.day}-${session.startMinute}`}>
            <span className="text-body-strong">
              {formatDate(session.day, {
                weekday: "short",
                month: "short",
                day: "numeric",
              })}
            </span>
            <span className="text-foreground-secondary">
              {` · ${formatSessionTimes(session)}`}
            </span>
          </dd>
        ))}
      </div>
      {where && (
        <div className="flex flex-col gap-0.5">
          <dt className="text-caption text-foreground-tertiary">Where</dt>
          <dd>{where}</dd>
        </div>
      )}
      {hasEventPricing && (
        <div className="flex flex-col gap-0.5">
          <dt className="text-caption text-foreground-tertiary">Pricing</dt>
          <dd>
            {bookingEvent.promotionName
              ? `${bookingEvent.promotionName}, locked in when you book`
              : "Event prices are locked in when you book"}
          </dd>
        </div>
      )}
    </dl>
  </div>
);

/**
 * The event this link books into, at the top of the booking sidebar.
 *
 * The clinic's own page stays as it is; this card is what tells the patient
 * why the menu is short and the days are few.
 */
export const BookingEventPass = ({ className }: { className?: string }) => {
  const event = useOpenEvent();
  if (!event) return null;

  return (
    <section
      aria-label="Event booking details"
      className={cx(
        "w-64 overflow-hidden rounded-xl border border-border bg-background shadow-md",
        className,
      )}
    >
      <div className="flex flex-col gap-0.5 bg-primary px-4 py-3 text-primary-foreground">
        <p className="text-overline text-primary-foreground">
          You&apos;re booking for
        </p>
        <h2 className="text-h4 text-primary-foreground">
          {event.bookingEvent.name}
        </h2>
      </div>
      <div className="px-4 py-3">
        <EventDetails {...event} />
      </div>
    </section>
  );
};

/**
 * The same event on a phone: one line across the top that opens to the
 * details, so the booking steps aren't pushed below the fold.
 */
export const BookingEventBar = ({ className }: { className?: string }) => {
  const event = useOpenEvent();
  const [open, setOpen] = useState(false);
  if (!event) return null;

  const { bookingEvent, hasEventPricing } = event;
  const summary = [
    summarizeSessions(bookingEvent.sessions),
    hasEventPricing ? "event prices" : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <section
      aria-label="Event booking details"
      className={cx("shrink-0 bg-primary text-primary-foreground", className)}
    >
      <button
        type="button"
        aria-expanded={open}
        aria-controls="booking-event-bar-details"
        onClick={() => setOpen((value) => !value)}
        className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
      >
        <span className="flex min-w-0 flex-col">
          <span className="truncate text-body-strong text-primary-foreground">
            {bookingEvent.name}
          </span>
          <span className="truncate text-caption text-primary-foreground">
            {summary}
          </span>
        </span>
        <FiChevronDown
          aria-hidden
          className={cx(
            "size-5 shrink-0 transition-transform",
            open && "rotate-180",
          )}
        />
      </button>
      {open && (
        <div
          id="booking-event-bar-details"
          className="bg-background px-4 py-3 text-foreground"
        >
          <EventDetails {...event} />
        </div>
      )}
    </section>
  );
};
